import type { PropsWithChildren, ReactNode } from 'react'
import { useState } from 'react'

import { AccordionStyled } from './styles/AccordionStyled'
import { Card } from '@ui/elements/Card'
import { Collapsible } from '@ui/elements/Collapsible'
import {
  IconChevronDown,
  IconChevronUp,
  type IconElement,
} from '@ui/elements/Icon'

export interface IAccordionProps {
  title: ReactNode
  isExpandedControlled?: boolean
  onChange?: (expanded?: boolean) => void
  collapsible?: boolean
  expandable?: boolean
  hoverable?: boolean
  expandIcon?: IconElement
  collapseIcon?: IconElement
}

export const Accordion = ({
  title,
  isExpandedControlled = false,
  onChange,
  collapsible = true,
  expandable = true,
  hoverable = false,
  expandIcon = <IconChevronDown />,
  collapseIcon = <IconChevronUp />,
  children,
}: PropsWithChildren<IAccordionProps>) => {
  const [isExpandedState, setIsExpandedState] = useState(isExpandedControlled)

  const isControlled = onChange !== undefined
  const isExpanded = isControlled ? isExpandedControlled : isExpandedState

  const toggleAccordion = () => {
    if (isControlled) {
      onChange(isExpanded)
    } else if ((isExpanded && collapsible) || (!isExpanded && expandable)) {
      setIsExpandedState(!isExpanded)
    }
  }

  return (
    <AccordionStyled
      expandable={expandable}
      collapsible={collapsible}
      hoverable={hoverable}
      isExpanded={isExpanded}
    >
      <Card>
        <Card.Content>
          <Collapsible
            title={title}
            iconPosition='right'
            expandIcon={expandIcon}
            collapseIcon={collapseIcon}
            defaultOpen={isExpandedControlled}
            open={isExpanded}
            onChange={toggleAccordion}
            animated
          >
            <Collapsible.Content>{children}</Collapsible.Content>
          </Collapsible>
        </Card.Content>
      </Card>
    </AccordionStyled>
  )
}

Accordion.displayName = 'Accordion'
